import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AiKeysTable from '../Components/AiKeysTable';
import { useAlert } from '../Components/AlertContext';
import AnalyticsExpandableWrapper from '../Components/AnalyticsExpandableRow';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import DateRangeSelector from '../Components/DateRangeSelector';
import '../Components/OrganizationDetail.css';
import OrganizationSettingsTab from '../Components/OrganizationSettingsTab';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';
import { Tab, Tabs } from '../Components/Tabs';
import '../Components/TotalAnalytics.css';
import { useAuth } from '../Contexts/AuthContext';
import { AiKeyApiProvider } from '../Services/AiKeyApiProvider';
import { useChatAnalyticsApi } from '../Services/ChatAnalyticsApiProvider';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import { AgentApiProvider } from '../Services/AgentApiProvider';
import AgentMetricsPanel from '../Components/AgentMetrics/AgentMetricsPanel';
import useLatestRequest from '../Hooks/useLatestRequest';
import useTablePagination from '../Hooks/useTablePagination';

const ORGANIZATION_TABS = {
  OVERVIEW: 'overview',
  SETTINGS: 'settings',
  AI_KEYS: 'ai-keys',
};

const ANALYTICS_TABS = {
  CHANNELS: 'channels',
  AGENTS: 'agents',
};

const ANALYTICS_TAB_KEY = 'org_active_tab';

function OrganizationContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { organizationId } = useParams();
  const { status } = useAuth();
  const permissions = status.permissions || [];

  const canManageAiKeys = permissions.includes('all:organization');

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab === ORGANIZATION_TABS.SETTINGS
      ? ORGANIZATION_TABS.SETTINGS
      : canManageAiKeys && requestedTab === ORGANIZATION_TABS.AI_KEYS
        ? ORGANIZATION_TABS.AI_KEYS
        : ORGANIZATION_TABS.OVERVIEW;

  const handleTabChange = useCallback(
    (value) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === ORGANIZATION_TABS.OVERVIEW) nextParams.delete('tab');
      else nextParams.set('tab', value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { getOrganizationById } = useOrganizationApi();
  const { getTotalChatAnalytics, getDetailedChatAnalytics } = useChatAnalyticsApi();

  const [organization, setOrganization] = useState();
  const [analytics, setAnalytics] = useState([]);
  const [detailedAnalytics, setDetailedAnalytics] = useState([]);

  const alertShownRef = useRef(false);

  const [totalStartDate, setTotalStartDate] = useState();
  const [totalEndDate, setTotalEndDate] = useState();

  const [detailedStartDate, setDetailedStartDate] = useState();
  const [detailedEndDate, setDetailedEndDate] = useState();

  const [totalChannels, setTotalChannels] = useState(0);

  const tableTitle = 'Organization channels';
  const pagination = useTablePagination(tableTitle);
  const beginTotalRequest = useLatestRequest();
  const beginDetailedRequest = useLatestRequest();

  const [analyticsTab, setAnalyticsTab] = useState(
    () => localStorage.getItem(ANALYTICS_TAB_KEY) || ANALYTICS_TABS.CHANNELS,
  );

  const columns = [
    { name: 'ID', selector: (row) => row.channel_id },
    { name: 'Channel Name', selector: (row) => row.channel_name },
    { name: 'Total Chats', selector: (row) => row.total_chats, sortable: true },
    { name: 'Active Chats', selector: (row) => row.active_chats, sortable: true },
    { name: 'Inactive Chats', selector: (row) => row.inactive_chats, sortable: true },
    { name: 'AI Chats', selector: (row) => row.communication_with_ai_message, sortable: true },
  ];

  useEffect(() => {
    if (location.state?.successMessage && !alertShownRef.current) {
      showAlert('success', location.state.successMessage);
      alertShownRef.current = true;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location, showAlert, navigate]);

  const fetchOrganization = useCallback(
    async (skipCache) => {
      try {
        const org = await getOrganizationById(organizationId, skipCache);
        setOrganization(org);
      } catch (error) {
        console.error('Error fetching organization:', error);
        navigate('/404');
      }
    },
    [organizationId, getOrganizationById, navigate],
  );

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const handleSettingsSaved = useCallback(() => {
    fetchOrganization(true);
  }, [fetchOrganization]);

  const handleSettingsCancel = useCallback(() => {
    handleTabChange(ORGANIZATION_TABS.OVERVIEW);
  }, [handleTabChange]);

  useEffect(() => {
    if (!totalStartDate) return;

    const fetchChatAnalytics = async () => {
      const isLatest = beginTotalRequest();
      try {
        const analyticsData = await getTotalChatAnalytics(totalStartDate, totalEndDate);
        if (!isLatest()) return;
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error fetching total analytics:', error);
      }
    };

    fetchChatAnalytics();
  }, [totalStartDate, totalEndDate, getTotalChatAnalytics, beginTotalRequest]);

  const fetchDetailedAnalytics = async (skip, limit) => {
    const isLatest = beginDetailedRequest();
    try {
      const response = await getDetailedChatAnalytics(
        detailedStartDate,
        detailedEndDate,
        skip,
        limit,
      );
      if (!isLatest()) return;
      setDetailedAnalytics(response.analyticsData);
      setTotalChannels(response.items);
    } catch (error) {
      console.error('Error fetching detailed analytics:', error);
    }
  };

  useEffect(() => {
    if (!detailedStartDate) return;
    const { skip, limit } = pagination.pageForFilters();
    fetchDetailedAnalytics(skip, limit);
  }, [detailedStartDate, detailedEndDate, getDetailedChatAnalytics]);

  const handlePeriodChange = useCallback((period) => {
    setTotalStartDate(period?.start_date);
    setTotalEndDate(period?.end_date);
  }, []);

  const handleDetailedPeriodChange = useCallback((period) => {
    setDetailedStartDate(period?.start_date);
    setDetailedEndDate(period?.end_date);
  }, []);

  const handleAnalyticsTabChange = (newValue) => {
    setAnalyticsTab(newValue);
    localStorage.setItem(ANALYTICS_TAB_KEY, newValue);
  };

  if (!organization) return <PageLoader />;

  const overviewTab = (
    <div className="row d-flex gap">
      <div className="radius-yes p-2 bg-yes col-12">
        <div className="org-details-header">
          <h2>Organization Details</h2>
        </div>

        <div className="org-header">
          <h2>{organization.title}</h2>
          <span className={`status-badge ${organization.is_active ? 'active' : 'inactive'}`}>
            {organization.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="detail-item">
          <label>ID:</label>
          <span>{organization.id}</span>
        </div>
        <div className="detail-item">
          <label>Description:</label>
          <p>{organization.description || 'No description available.'}</p>
        </div>
        <div className="detail-item">
          <label>Billing</label>
          <p>{organization?.is_billed === true ? 'Billed' : 'Not billed'}</p>
        </div>
        <div className="detail-item">
          <label>Active Communications:</label>
          <p>{organization?.active_communications}</p>
        </div>
        <div className="detail-item">
          <label>Communications Per Day:</label>
          <p>{organization?.communications_per_day}</p>
        </div>
        <div className="detail-item">
          <label>Communications Per Month:</label>
          <p>{organization?.communications_per_month}</p>
        </div>
      </div>

      <div className="radius-yes p-2 bg-yes col-12">
        <h2 style={{ fontWeight: 'normal' }}>Total Analytics</h2>
        {!analytics ? (
          <div>Loading analytics...</div>
        ) : (
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Total Chats</h3>
              </div>
              <div className="analytics-value">{analytics.total_chats?.toLocaleString() || 0}</div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Open chats</span>
                  <span className="value">{analytics.active_chats?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Chats with AI message</span>
                  <span className="value">
                    {analytics.communication_with_ai_message?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Total Messages</h3>
              </div>
              <div className="analytics-value">
                {analytics.total_messages?.toLocaleString() || 0}
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Incoming</span>
                  <span className="value">{analytics.input_messages?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Outgoing</span>
                  <span className="value">{analytics.output_messages?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-header">
                <h3>AI Statistics</h3>
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Generated messages</span>
                  <span className="value">
                    {analytics.generated_messages?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Sent messages</span>
                  <span className="value">{analytics.is_sent?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Redacted messages</span>
                  <span className="value">{analytics.is_redacted?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '6vh' }}>
          <DateRangeSelector storageKey="org-total" onChange={handlePeriodChange} />
        </div>
      </div>

      <Tabs activeTab={analyticsTab} onTabChange={handleAnalyticsTabChange}>
        <Tab label="Channels" value={ANALYTICS_TABS.CHANNELS}>
          <div className="radius-yes p-2 bg-yes col-12">
            {totalChannels === 0 ? (
              <p>No detailed data available.</p>
            ) : (
              <Table
                title={tableTitle}
                columns={columns}
                data={detailedAnalytics}
                selectableRowsHighlight
                expandableRows
                expandableRowsComponent={AnalyticsExpandableWrapper}
                paginationPerPage={pagination.initialPageSize}
                resetPaginationToggle={pagination.resetToggle}
                paginationTotalRows={totalChannels}
                onChangePage={fetchDetailedAnalytics}
              />
            )}
            <DateRangeSelector storageKey="org-channel" onChange={handleDetailedPeriodChange} />
          </div>
        </Tab>
        <Tab label="Agents" value={ANALYTICS_TABS.AGENTS}>
          <div className="radius-yes p-2 bg-yes col-12">
            <AgentMetricsPanel organizationId={organizationId} />
          </div>
        </Tab>
      </Tabs>
    </div>
  );

  return (
    <div className="container">
      <Breadcrumbs />
      <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
        <Tab label="Overview" value={ORGANIZATION_TABS.OVERVIEW}>
          {overviewTab}
        </Tab>
        <Tab label="Settings" value={ORGANIZATION_TABS.SETTINGS} wrapText={false}>
          <div className="row d-flex gap w-100">
            <OrganizationSettingsTab
              organizationId={organizationId}
              onSaved={handleSettingsSaved}
              onCancel={handleSettingsCancel}
            />
          </div>
        </Tab>
        {canManageAiKeys && (
          <Tab label="AI Keys" value={ORGANIZATION_TABS.AI_KEYS}>
            <div className="row d-flex gap w-100">
              <div className="radius-yes p-2 bg-yes col-12">
                <AiKeyApiProvider>
                  <AiKeysTable />
                </AiKeyApiProvider>
              </div>
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  );
}

export default function Organization() {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <OrganizationContent />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}
